/**
 * NVR 이 읽는 BIFF8 .xls 작성기.
 *
 * SheetJS 의 `bookType:'xls'` 산출물은 셀 내용이 같아도 NVR 원격등록 임포터가 거부한다
 * (2026-09-02 첨단 현장 확인). SheetJS 는 문자열을 LABEL(0x0204) 인라인으로, 숫자를
 * NUMBER 로 쓰고 ROW 레코드를 내지 않는데, 등록이 확인된 파일(업체 Excel 저장본,
 * offline-site-kit/cctv-field/nvr_common.py 의 xlwt 산출물)은 전부 LABELSST+SST, RK,
 * ROW 를 쓴다. 어느 레코드가 결정적인지는 NVR 쪽 파서를 볼 수 없어 알 수 없으므로,
 * 검증 가능한 유일한 기준인 **xlwt 1.3.0 `Workbook(encoding='utf-8')` 기본 스타일 산출물과
 * 바이트 단위로 동일**하게 쓴다. 레코드 순서·고정값·SST 분할·OLE2 컨테이너 배치까지
 * xlwt 를 그대로 따르며, biff8Xls.test.ts 가 write_xls 로 만든 fixture 와 바이트 비교한다.
 *
 * 지원 범위는 NVR 템플릿에 필요한 만큼만: 시트 1개, 기본 스타일, 문자열·숫자·빈 셀.
 * 입력 계약(writeBiff8Xls 가 검증한다): 첫 행은 비어 있지 않고, 행 65536개·열 256개·문자열 32767자
 * 이내이며, 결과 스트림이 약 7MB 를 넘지 않는다. 계약 밖 입력에서 xlwt 와 갈리는 부분은
 * 흉내내는 대신 거부한다 — 조용히 다른 파일을 내느니 터지는 편이 낫다.
 */

/** 셀 값. `null`·`''` 은 xlwt 처럼 BLANK 셀로 쓴다. */
export type XlsCellValue = string | number | null

const SECTOR = 0x200
const SST_PIECE_LIMIT = 0x2020
const CELL_XF = 0x11 // xlwt 기본 스타일의 셀 XF 인덱스 (스타일 XF 16개 뒤 두 번째)
const MAX_STR_LEN = 32767

// ---- 바이트 도구 ----------------------------------------------------------

const u16 = (n: number): number[] => [n & 0xff, (n >>> 8) & 0xff]
const i32 = (n: number): number[] => [n & 0xff, (n >>> 8) & 0xff, (n >>> 16) & 0xff, (n >>> 24) & 0xff]
function f64(x: number): number[] {
  const view = new DataView(new ArrayBuffer(8))
  view.setFloat64(0, x, true)
  return Array.from(new Uint8Array(view.buffer))
}

function concat(chunks: ArrayLike<number>[]): Uint8Array {
  const total = chunks.reduce((sum, c) => sum + c.length, 0)
  const out = new Uint8Array(total)
  let offset = 0
  for (const c of chunks) {
    out.set(c, offset)
    offset += c.length
  }
  return out
}

/** BIFF 레코드: id(2) + length(2) + body */
function rec(id: number, body: ArrayLike<number>): Uint8Array {
  return concat([u16(id), u16(body.length), body])
}

/** 문자를 8비트(latin1)로 담을 수 있는지 — xlwt upack2 의 "compressed" 판정과 동일 */
function isLatin1(s: string): boolean {
  for (let i = 0; i < s.length; i++) if (s.charCodeAt(i) > 0xff) return false
  return true
}

/** 문자 바이트열 + 플래그: compressed 면 (0, 8비트), 아니면 (1, UTF-16LE) */
function packChars(s: string): { flag: number; count: number; bytes: number[] } {
  // xlwt 는 코드포인트로 센다(UnicodeUtils.upack2). JS 의 .length 는 UTF-16 단위라 서로게이트에서 갈린다.
  if (s.length > MAX_STR_LEN && Array.from(s).length > MAX_STR_LEN) {
    throw new Error(`String longer than ${MAX_STR_LEN} characters`)
  }
  if (isLatin1(s)) {
    return { flag: 0, count: s.length, bytes: Array.from(s, ch => ch.charCodeAt(0)) }
  }
  const bytes: number[] = []
  for (let i = 0; i < s.length; i++) bytes.push(...u16(s.charCodeAt(i)))
  return { flag: 1, count: s.length, bytes } // count = UTF-16 code unit 수 (xlwt: len(utf_16_le)//2)
}

/** xlwt upack2: 길이 2바이트 */
function upack2(s: string): number[] {
  const { flag, count, bytes } = packChars(s)
  return [...u16(count), flag, ...bytes]
}

/** xlwt upack1: 길이 1바이트 (시트명·폰트명) */
function upack1(s: string): number[] {
  const { flag, count, bytes } = packChars(s)
  return [count & 0xff, flag, ...bytes]
}

// ---- SST (공유 문자열 표) --------------------------------------------------

/**
 * xlwt SharedStringTable 과 같은 방식으로 문자열을 모으고, 8224(0x2020)바이트 조각으로
 * 나눠 SST + CONTINUE 레코드를 만든다. 문자열 헤더(길이·플래그·첫 글자)는 조각을
 * 넘지 않게 통째로 옮기고, 본문은 조각 경계에서 잘라 다음 조각 첫 바이트에 플래그를 다시 쓴다.
 */
class SharedStringTable {
  private readonly indexOf = new Map<string, number>()
  private refCount = 0

  add(s: string): number {
    this.refCount++
    const existing = this.indexOf.get(s)
    if (existing !== undefined) return existing
    const idx = this.indexOf.size
    this.indexOf.set(s, idx)
    return idx
  }

  records(): Uint8Array {
    const pieces: number[][] = []
    let current: number[] = [0, 0, 0, 0, 0, 0, 0, 0] // 첫 조각 머리: 총 참조수·고유 문자열 수 자리

    const newPiece = () => {
      pieces.push(current)
      current = []
    }
    const saveAtom = (bytes: number[]) => {
      if (SST_PIECE_LIMIT - current.length < bytes.length) newPiece()
      current.push(...bytes)
    }
    const saveSplit = (bytes: number[], unicode: boolean) => {
      let i = 0
      while (i < bytes.length) {
        const free = SST_PIECE_LIMIT - current.length
        const tail = bytes.length - i
        const needMore = free < tail
        const atom = needMore ? (unicode ? free & 0xfffe : free) : tail
        for (let k = 0; k < atom; k++) current.push(bytes[i + k])
        if (needMore) {
          newPiece()
          current.push(unicode ? 1 : 0)
        }
        i += atom
      }
    }

    for (const s of this.indexOf.keys()) {
      const packed = upack2(s)
      const unicode = packed[2] === 1
      const head = unicode ? 5 : 4 // 길이(2)+플래그(1)+첫 글자(1 또는 2)
      saveAtom(packed.slice(0, head))
      saveSplit(packed.slice(head), unicode)
    }
    newPiece()

    const [first, ...continues] = pieces
    const sst = concat([
      u16(0x00fc), u16(first.length),
      i32(this.refCount), i32(this.indexOf.size),
      first.slice(8),
    ])
    return concat([sst, ...continues.map(p => rec(0x003c, p))])
  }
}

// ---- 셀 레코드 -------------------------------------------------------------

type Cell =
  | { kind: 'str'; sst: number }
  | { kind: 'rk'; rk: number }
  | { kind: 'num'; value: number }
  | { kind: 'blank' }

/** xlwt NumberCell.get_encoded_data: 30비트 정수 → RK(2), 100배가 30비트 정수 → RK(3), 그 외 NUMBER */
function numberCell(value: number): Cell {
  const fits30 = (n: number) => n >= -0x20000000 && n < 0x20000000
  if (fits30(value) && Number.isInteger(value)) return { kind: 'rk', rk: 2 | (value << 2) }
  const hundred = value * 100
  if (fits30(hundred)) {
    const rounded = Math.round(hundred)
    if (rounded / 100 === value) return { kind: 'rk', rk: 3 | (rounded << 2) }
  }
  return { kind: 'num', value }
}

function toCell(value: XlsCellValue | undefined, sst: SharedStringTable): Cell {
  if (typeof value === 'number') return numberCell(value)
  if (typeof value === 'string' && value.length > 0) return { kind: 'str', sst: sst.add(value) }
  return { kind: 'blank' }
}

/** xlwt Row.get_row_biff_data: 기본 높이 0xFF, 옵션 = bit8 | 행 XF 0x0F<<16 */
function rowRecord(rowx: number, lastCol: number): Uint8Array {
  return rec(0x0208, [...u16(rowx), ...u16(0), ...u16(lastCol + 1), ...u16(0x00ff), ...u16(0), ...u16(0), ...i32(0x000f0100)])
}

/**
 * xlwt Cell._get_cells_biff_data_mul: 이웃한 RK 는 MULRK, 이웃한 BLANK 는 MULBLANK 로 묶는다.
 * RK 묶음 도중 RK 로 못 쓰는 NUMBER 가 나오면 묶음을 닫고 그 NUMBER 를 뒤에 붙인다.
 * cells 는 0열부터 빈 곳 없이 채워진 배열이라 열 인접성은 인덱스로 보장된다.
 */
function cellRecords(rowx: number, cells: Cell[]): Uint8Array {
  const pieces: Uint8Array[] = []
  const numberRec = (col: number, value: number) => rec(0x0203, [...u16(rowx), ...u16(col), ...u16(CELL_XF), ...f64(value)])

  let i = 0
  while (i < cells.length) {
    const cell = cells[i]
    if (cell.kind === 'num') {
      pieces.push(numberRec(i, cell.value))
      i++
      continue
    }
    if (cell.kind === 'str') {
      pieces.push(rec(0x00fd, [...u16(rowx), ...u16(i), ...u16(CELL_XF), ...i32(cell.sst)]))
      i++
      continue
    }

    const target = cell.kind
    const group: Cell[] = [cell]
    let trailingNumber: Uint8Array | null = null
    let next = cells.length
    for (let j = i + 1; j < cells.length; j++) {
      const c = cells[j]
      if (c.kind === target) {
        group.push(c)
        continue
      }
      if (target === 'rk' && c.kind === 'num') {
        trailingNumber = numberRec(j, c.value)
        next = j + 1
      } else {
        next = j
      }
      break
    }
    const lastCol = i + group.length - 1

    if (target === 'rk') {
      const rks = group.map(c => (c as { rk: number }).rk)
      if (group.length === 1) {
        pieces.push(rec(0x027e, [...u16(rowx), ...u16(i), ...u16(CELL_XF), ...i32(rks[0])]))
      } else {
        pieces.push(rec(0x00bd, [...u16(rowx), ...u16(i), ...rks.flatMap(rk => [...u16(CELL_XF), ...i32(rk)]), ...u16(lastCol)]))
      }
    } else if (group.length === 1) {
      pieces.push(rec(0x0201, [...u16(rowx), ...u16(i), ...u16(CELL_XF)]))
    } else {
      pieces.push(rec(0x00be, [...u16(rowx), ...u16(i), ...group.flatMap(() => u16(CELL_XF)), ...u16(lastCol)]))
    }
    if (trailingNumber) pieces.push(trailingNumber)
    i = next
  }
  return concat(pieces)
}

// ---- 워크북 스트림 ---------------------------------------------------------

const BOF_GLOBALS = [0x00, 0x06, 0x05, 0x00, 0xbb, 0x0d, 0xcc, 0x07, 0x00, 0x00, 0x00, 0x00, 0x06, 0x00, 0x00, 0x00]
const BOF_SHEET = [0x00, 0x06, 0x10, 0x00, 0xbb, 0x0d, 0xcc, 0x07, 0x00, 0x00, 0x00, 0x00, 0x06, 0x00, 0x00, 0x00]
const FONT_ARIAL = [0xc8, 0x00, 0x00, 0x00, 0xff, 0x7f, 0x90, 0x01, 0x00, 0x00, 0x00, 0x00, 0x01, 0x00, ...upack1('Arial')]
const XF_STYLE = [0x06, 0x00, 0xa4, 0x00, 0xf5, 0xff, 0x20, 0x00, 0x00, 0xf4, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0xc0, 0x20]
const XF_CELL_FONT6 = [0x06, 0x00, 0xa4, 0x00, 0x01, 0x00, 0x20, 0x00, 0x00, 0xf8, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0xc0, 0x20]
const XF_CELL_FONT7 = [0x07, 0x00, 0xa4, 0x00, 0x01, 0x00, 0x20, 0x00, 0x00, 0xf8, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0xc0, 0x20]
const WINDOW1 = [0xe0, 0x01, 0x5a, 0x00, 0xcf, 0x3f, 0x4e, 0x2a, 0x38, 0x00, 0x00, 0x00, 0x00, 0x00, 0x01, 0x00, 0x58, 0x02]
const WINDOW2_SELECTED = [0xb6, 0x02, ...u16(0), ...u16(0), ...i32(0x40), ...u16(0), ...u16(0), ...i32(0)]
const SETUP_A4 = [
  ...u16(9), ...u16(100), ...u16(1), ...u16(1), ...u16(1), ...u16(0x0083), ...u16(300), ...u16(300),
  ...f64(0.1), ...f64(0.1), ...u16(1),
]

/** xlwt Workbook.get_biff_data 의 BOUNDSHEET 앞부분 — 스타일이 고정이라 내용도 고정 */
function globalsBeforeBoundsheet(): Uint8Array {
  const two = (v: number) => u16(v)
  return concat([
    rec(0x0809, BOF_GLOBALS),
    rec(0x00e1, two(0x04b0)), // INTERFACEHDR (codepage 1200)
    rec(0x00c1, two(0)), // MMS
    rec(0x00e2, []), // INTERFACEEND
    rec(0x005c, Array.from('None'.padEnd(112, ' '), ch => ch.charCodeAt(0))), // WRITEACCESS
    rec(0x0042, two(0x04b0)), // CODEPAGE
    rec(0x0161, two(0)), // DSF
    rec(0x013d, two(1)), // TABID
    rec(0x009c, two(0x0e)), // FNGROUPCOUNT
    rec(0x0019, two(0)), // WINDOWPROTECT
    rec(0x0012, two(0)), // PROTECT
    rec(0x0063, two(0)), // OBJECTPROTECT
    rec(0x0013, two(0)), // PASSWORD
    rec(0x01af, two(0)), // PROT4REV
    rec(0x01bc, two(0)), // PROT4REVPASS
    rec(0x0040, two(0)), // BACKUP
    rec(0x008d, two(0)), // HIDEOBJ
    rec(0x003d, WINDOW1),
    rec(0x0022, two(0)), // DATEMODE
    rec(0x000e, two(1)), // PRECISION
    rec(0x01b7, two(0)), // REFRESHALL
    rec(0x00da, two(0)), // BOOKBOOL
    ...Array.from({ length: 7 }, () => rec(0x0031, FONT_ARIAL)), // xlwt 기본 폰트 0~3,5~7
    rec(0x041e, [...u16(0xa4), ...upack2('General')]), // FORMAT
    ...Array.from({ length: 16 }, () => rec(0x00e0, XF_STYLE)),
    rec(0x00e0, XF_CELL_FONT6),
    rec(0x00e0, XF_CELL_FONT7),
    rec(0x0293, [0x00, 0x80, 0x00, 0xff]), // STYLE
    rec(0x0160, two(1)), // USESELFS
  ])
}

/** xlwt Worksheet.get_biff_data 의 DIMENSIONS 앞·뒤 고정 레코드 */
function sheetHead(): Uint8Array {
  return concat([
    rec(0x0809, BOF_SHEET),
    rec(0x000d, u16(1)), // CALCMODE
    rec(0x000c, u16(100)), // CALCCOUNT
    rec(0x000f, u16(1)), // REFMODE
    rec(0x0011, u16(0)), // ITERATION
    rec(0x0010, f64(0.001)), // DELTA
    rec(0x005f, u16(0)), // SAVERECALC
    rec(0x0080, [...u16(0), ...u16(0), ...u16(1), ...u16(0)]), // GUTS
    rec(0x0225, [...u16(0), ...u16(0xff)]), // DEFAULTROWHEIGHT
    rec(0x0081, u16(0x0c01)), // WSBOOL
  ])
}

function sheetAfterDimensions(): Uint8Array {
  return concat([
    rec(0x002a, u16(0)), // PRINTHEADERS
    rec(0x002b, u16(0)), // PRINTGRIDLINES
    rec(0x0082, u16(1)), // GRIDSET
    rec(0x001b, u16(0)), // HORIZONTALPAGEBREAKS
    rec(0x001a, u16(0)), // VERTICALPAGEBREAKS
    rec(0x0014, upack2('&P')), // HEADER
    rec(0x0015, upack2('&F')), // FOOTER
    rec(0x0083, u16(1)), // HCENTER
    rec(0x0084, u16(0)), // VCENTER
    rec(0x0026, f64(0.3)), // LEFTMARGIN
    rec(0x0027, f64(0.3)), // RIGHTMARGIN
    rec(0x0028, f64(0.61)), // TOPMARGIN
    rec(0x0029, f64(0.37)), // BOTTOMMARGIN
    rec(0x00a1, SETUP_A4),
    rec(0x0012, u16(0)), // PROTECT
    rec(0x00dd, u16(0)), // SCENPROTECT
    rec(0x0019, u16(0)), // WINDOWPROTECT
    rec(0x0063, u16(0)), // OBJECTPROTECT
    rec(0x0013, u16(0)), // PASSWORD
  ])
}

/** xlwt: (첫 쓰인 행, 끝행+1, 첫 쓰인 열, 끝열+1). 계약상 첫 행·첫 열은 항상 0 이다. */
function dimensionsRecord(rowCount: number, maxCol: number): Uint8Array {
  return rec(0x0200, [...i32(0), ...i32(rowCount), ...u16(0), ...u16(maxCol + 1), ...u16(0)])
}

// ---- OLE2 컨테이너 (xlwt CompoundDoc.XlsDoc) --------------------------------

const SID_FREE = -1
const SID_END_OF_CHAIN = -2
const SID_USED_BY_SAT = -3

const DIRECTORY_SECTORS = 1 // 항목 4개(Root Entry·Workbook·빈 2개) × 128바이트 = 정확히 한 섹터
const MAX_SAT_SECTORS = 109 // MSAT 첫 표에 담기는 수. 넘으면 xlwt 는 2차 표를 쓰지만 여기선 거부한다.

function directoryEntry(name: string, type: number, rootChild: number, startSid: number, streamSize: number): number[] {
  const nameBytes = name.length > 0 ? [...Array.from(name, ch => u16(ch.charCodeAt(0))).flat(), 0, 0] : []
  return [
    ...nameBytes, ...new Array(64 - nameBytes.length).fill(0),
    ...u16(nameBytes.length), type, 0x01 /* colour: black */,
    ...i32(-1), ...i32(-1), ...i32(rootChild),
    ...new Array(9 * 4).fill(0), // CLSID(16) + user flags(4) + 타임스탬프(16)
    ...i32(startSid), ...i32(streamSize), ...i32(0),
  ]
}

/** Workbook 스트림 하나를 xlwt 와 같은 섹터 배치의 OLE2 문서로 감싼다. */
function wrapCompoundDocument(stream: Uint8Array): Uint8Array {
  const padding = 0x1000 - (stream.length % 0x1000) // xlwt: 나머지가 0 이어도 0x1000 을 덧댄다
  const bookLen = stream.length + padding
  const directory = concat([
    directoryEntry('Root Entry', 0x05, 1, SID_END_OF_CHAIN, 0),
    directoryEntry('Workbook', 0x02, -1, 0, bookLen),
    directoryEntry('', 0x00, -1, SID_END_OF_CHAIN, 0),
    directoryEntry('', 0x00, -1, SID_END_OF_CHAIN, 0),
  ])
  if (directory.length !== SECTOR) throw new Error(`OLE2 directory must be one sector, got ${directory.length}`)

  // SAT 는 자기 자신도 섹터를 쓰므로 개수가 늘면 총량도 는다 — xlwt 와 같은 방식으로 고정점을 찾는다.
  const bookSectors = bookLen >> 9
  let satSectors = 0
  let total = bookSectors + DIRECTORY_SECTORS
  while (total > 128 * satSectors) {
    satSectors++
    total++
  }
  if (satSectors > MAX_SAT_SECTORS) {
    throw new Error(`.xls stream too large: ${bookLen} bytes needs ${satSectors} SAT sectors (max ${MAX_SAT_SECTORS})`)
  }

  const sat = new Array<number>(128 * satSectors).fill(SID_FREE)
  let sect = 0
  for (; sect < bookSectors - 1; sect++) sat[sect] = sect + 1
  sat[sect++] = SID_END_OF_CHAIN
  const firstSatSid = sect
  for (; sect < bookSectors + satSectors; sect++) sat[sect] = SID_USED_BY_SAT
  const dirSid = sect
  sat[sect] = SID_END_OF_CHAIN

  const msat = new Array<number>(MAX_SAT_SECTORS).fill(SID_FREE)
  for (let i = 0; i < satSectors; i++) msat[i] = firstSatSid + i

  const header = [
    0xd0, 0xcf, 0x11, 0xe0, 0xa1, 0xb1, 0x1a, 0xe1, // magic
    ...new Array(16).fill(0), // CLSID
    ...u16(0x003e), ...u16(0x0003), 0xfe, 0xff, // minor·major 버전, 바이트 순서
    ...u16(9), ...u16(6), // 섹터 512, 미니 섹터 64
    ...new Array(10).fill(0),
    ...i32(satSectors), ...i32(dirSid), ...i32(0),
    ...i32(0x1000), // 미니 스트림 기준 크기
    ...i32(SID_END_OF_CHAIN), ...i32(0), // SSAT 없음
    ...i32(SID_END_OF_CHAIN), ...i32(0), // MSAT 2차 표 없음 (SAT 109섹터 이하)
    ...msat.flatMap(i32),
  ]
  if (header.length !== SECTOR) throw new Error(`OLE2 header must be ${SECTOR} bytes, got ${header.length}`)

  return concat([header, stream, new Uint8Array(padding), sat.flatMap(i32), directory])
}

// ---- 공개 API --------------------------------------------------------------

const MAX_ROWS = 65536 // xlwt Row.__init__ 이 rowx > 65535 를 거부한다
const MAX_COLS = 256 // xlwt Row.__adjust_bound_col_idx 가 열 인덱스 > 255 를 거부한다

/**
 * 행 배열을 시트 하나짜리 BIFF8 .xls 로 쓴다. 각 행은 0열부터 채운 값 배열이며
 * 빈 행([])은 xlwt 처럼 ROW 레코드를 만들지 않는다. 첫 행은 비어 있으면 안 된다 —
 * xlwt 는 DIMENSIONS 의 첫 행·열을 실제로 쓰인 최소 인덱스로 적고 셀이 하나도 없으면
 * FONT/XF 까지 덜 내는데, 그 갈래를 흉내내는 대신 거부한다(NVR 행은 항상 헤더로 시작한다).
 */
export function writeBiff8Xls(rows: XlsCellValue[][], sheetName = 'Sheet1'): Uint8Array {
  if (rows.length === 0 || rows[0].length === 0) throw new Error('first row must have at least one cell')
  if (rows.length > MAX_ROWS) throw new Error(`too many rows: ${rows.length} (max ${MAX_ROWS})`)

  const sst = new SharedStringTable()
  const rowBlocks: Uint8Array[] = []
  let maxCol = 0
  let usedRows = 0
  rows.forEach((values, rowx) => {
    if (values.length === 0) return
    if (values.length > MAX_COLS) throw new Error(`row ${rowx} has ${values.length} cells (max ${MAX_COLS})`)
    const cells = values.map(v => toCell(v, sst))
    const lastCol = cells.length - 1
    maxCol = Math.max(maxCol, lastCol)
    usedRows = rowx + 1
    rowBlocks.push(rowRecord(rowx, lastCol), cellRecords(rowx, cells))
  })

  const globals = globalsBeforeBoundsheet()
  const sstRecords = sst.records()
  const eof = rec(0x000a, [])
  const boundsheetLen = 4 + 6 + upack1(sheetName).length
  const sheetOffset = globals.length + boundsheetLen + sstRecords.length + eof.length
  const boundsheet = rec(0x0085, [...i32(sheetOffset), 0x00 /* visible */, 0x00 /* worksheet */, ...upack1(sheetName)])

  const sheet = concat([
    sheetHead(),
    dimensionsRecord(usedRows, maxCol),
    sheetAfterDimensions(),
    ...rowBlocks,
    rec(0x023e, WINDOW2_SELECTED),
    eof,
  ])

  return wrapCompoundDocument(concat([globals, boundsheet, sstRecords, eof, sheet]))
}
